import {DragEvent, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
  BookOpen, ChevronDown, ChevronLeft, ChevronRight, Combine, Copy, Download, ExternalLink, Eye, EyeOff, LoaderCircle, MousePointer2, Redo2, RefreshCw, RotateCcw,
  Save, Split, SquarePlus, Trash2, Undo2, Upload, ZoomIn, ZoomOut,
} from 'lucide-react';
import {CONFIG} from '../lib/config';
import {ocrFetch} from './ocrFetch';
import {
  cloneRegions, createRegion, drawBox, mergeRegions, moveRegion, normalizedPoint, regionsEqual,
  resizeRegion, ResizeHandle, sortRegions, splitRegion, SplitAxis,
} from '../lib/ocrEditor';
import {
  loadOcrDraft, OcrDraftPage, removeOcrDraft, saveOcrDraft,
} from '../lib/ocrDrafts';
import {OcrDocument, OcrPage, OcrRegion, OcrSession, SessionMeta} from '../lib/types';

const ACCEPTED_FILES = '.png,.jpg,.jpeg,.webp,.pdf,.tif,.tiff';
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const PAGE_CONCURRENCY = 4;
const HISTORY_LIMIT = 50;
const REGION_TYPES: OcrRegion['type'][] = ['handwritten', 'printed', 'formula', 'table', 'annotation', 'image', 'graph'];
const REGION_COLORS: Record<OcrRegion['type'], string> = {
  handwritten: '#4ecdc4', printed: '#f0d040', formula: '#a882ff', table: '#e87030',
  annotation: '#70b7ff', image: '#ef767a', graph: '#83d17a',
};
const REGION_LABELS: Record<OcrRegion['type'], string> = {
  handwritten: 'Рукописний текст', printed: 'Друкований текст', formula: 'Формула', table: 'Таблиця',
  annotation: 'Примітка', image: 'Зображення', graph: 'Графік',
};
const PAGE_STATUS_LABELS: Record<OcrPage['status'], string> = {
  queued: 'очікує обробки', processing: 'розпізнається', completed: 'готова',
  failed: 'помилка розпізнавання', quota_paused: 'призупинена через квоту',
};

type OcrStreamFailure = Error & {status?: number; retryAfter?: number};

export async function readOcrStream(response: Response, onRegion: (region: OcrRegion) => void): Promise<OcrSession> {
  if (!response.body) return response.json() as Promise<OcrSession>;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const {done, value} = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), {stream: !done});
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
      const event = block.match(/^event:\s*(.+)$/m)?.[1];
      const data = block.match(/^data:\s*(.+)$/m)?.[1];
      if (!event || !data) continue;
      const payload = JSON.parse(data);
      if (event === 'region' && payload.region) onRegion(payload.region as OcrRegion);
      if (event === 'complete' && payload.session) return payload.session as OcrSession;
      if (event === 'error') {
        const error = Object.assign(new Error(payload.message || 'Не вдалося розпізнати сторінку.'), {
          status: payload.status, retryAfter: payload.retry_after,
        }) as OcrStreamFailure;
        throw error;
      }
    }
    if (done) break;
  }
  throw new Error('OCR-потік завершився без фінального результату.');
}

const OCR_EXAMPLES = [
  {
    id: 'lesya-ukrainka-korolivna-1901',
    title: 'Королівна (1901)',
    author: 'Леся Українка',
    asset: '/ocr-examples/lesya-ukrainka-korolivna-1901.jpg',
    preview: '/ocr-examples/lesya-ukrainka-korolivna-1901.jpg',
    fileName: 'lesya-ukrainka-korolivna-1901.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://www.l-ukrainka.name/uk/Gallery/Works/Korolivna1901.html',
  },
  {
    id: 'diplomatic-passport-archive',
    title: 'Дипломатичний паспорт',
    author: 'Електронний архів МЗС України',
    asset: '/ocr-examples/3696-1-62-ark89.jpg',
    preview: '/ocr-examples/3696-1-62-ark89-preview.jpg',
    fileName: '3696-1-62-ark89.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://earchive.mfa.gov.ua/files/documents-earchive/Diplomatic-passports/3696-1-62-ark89.jpg',
  },
  {
    id: 'rukopys-geometry-cone',
    title: 'Геометрія: конус',
    author: 'Шкільний зошит',
    asset: '/ocr-examples/rukopys-geometry-cone.jpg',
    preview: '/ocr-examples/rukopys-geometry-cone-preview.jpg',
    fileName: 'rukopys-geometry-cone.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://huggingface.co/datasets/UkrainianCatholicUniversity/rukopys/viewer/full/train?row=773',
  },
  {
    id: 'tychyna-ia-utverdzhuius',
    title: 'Я утверждаюсь',
    author: 'Павло Тичина',
    asset: '/ocr-examples/tychyna-ia-utverdzhuius.jpg',
    preview: '/ocr-examples/tychyna-ia-utverdzhuius.jpg',
    fileName: 'tychyna-ia-utverdzhuius.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://www.nbuv.gov.ua/node/5538',
  },
  {
    id: 'shevchenko-dumy-moyi',
    title: 'Думи мої (1847)',
    author: 'Тарас Шевченко',
    asset: '/ocr-examples/shevchenko-dumy-moyi.jpg',
    preview: '/ocr-examples/shevchenko-dumy-moyi.jpg',
    fileName: 'shevchenko-dumy-moyi.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Dumy_moji_shevchenko.jpg',
  },
  {
    id: 'radiodyktant-treba-zhyty',
    title: 'Треба жити!',
    author: 'Радіодиктант 2025',
    asset: '/ocr-examples/radiodyktant-treba-zhyty.jpg',
    preview: '/ocr-examples/radiodyktant-treba-zhyty.jpg',
    fileName: 'radiodyktant-treba-zhyty.jpg',
    mimeType: 'image/jpeg',
    sourceUrl: 'https://huggingface.co/datasets/UkrainianCatholicUniversity/rukopys',
  },
] as const;
const RESIZE_HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

type DraftPages = Record<string, OcrDraftPage>;
type Drafts = Record<string, DraftPages>;
type EditorTool = 'select' | 'draw' | 'split';
interface HistoryState {past: DraftPages[]; future: DraftPages[]}
interface Gesture {
  mode: 'move' | 'resize' | 'draw' | 'split';
  pointerId: number;
  start: [number, number];
  region?: OcrRegion;
  handle?: ResizeHandle;
  baseRegions: OcrRegion[];
}

interface Props {
  session: SessionMeta;
  onUpdateSession: (session: SessionMeta) => void;
  onDirtyChange?: (dirty: boolean) => void;
  userId: string;
  apiKey?: string;
}

function cloneDraftPages(pages: DraftPages): DraftPages {
  return Object.fromEntries(Object.entries(pages).map(([pageId, page]) => [pageId, {
    baseRevision: page.baseRevision,
    regions: cloneRegions(page.regions),
  }]));
}

function replacePage(current: OcrSession | null, pageId: string, page: OcrPage): OcrSession | null {
  if (!current) return current;
  return {...current, documents: current.documents.map(document => ({
    ...document, pages: document.pages.map(candidate => candidate.id === pageId ? page : candidate),
  }))};
}

function replaceDocument(current: OcrSession | null, replacement: OcrDocument): OcrSession | null {
  return current ? {...current, documents: current.documents.map(document => document.id === replacement.id ? replacement : document)} : current;
}

function findPage(data: OcrSession, pageId: string): OcrPage | undefined {
  return data.documents.flatMap(document => document.pages).find(page => page.id === pageId);
}

function effectiveRegions(page: OcrPage, drafts?: DraftPages): OcrRegion[] {
  return drafts?.[page.id]?.regions || page.regions;
}

function effectiveDocument(document: OcrDocument, drafts?: DraftPages): OcrDocument {
  return {...document, pages: document.pages.map(page => ({...page, regions: effectiveRegions(page, drafts)}))};
}

function downloadBlob(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 1_000);
}

function download(name: string, content: string, type: string) {
  downloadBlob(name, new Blob([content], {type}));
}

function documentText(document: OcrDocument): string {
  return document.pages.map((page, index) => {
    const text = page.regions.map(region => region.text).filter(Boolean).join('\n');
    return document.page_count > 1 ? `Сторінка ${index + 1}\n${text}` : text;
  }).join('\n\n');
}

function documentJson(document: OcrDocument): string {
  return JSON.stringify(document.pages.map(page => ({page: page.page_index + 1, regions: page.regions})), null, 2);
}

async function responseError(response: Response, fallback: string): Promise<string> {
  const body = await response.text();
  try {
    const detail = JSON.parse(body)?.detail;
    if (typeof detail === 'string') return detail;
    if (typeof detail?.message === 'string') return detail.message;
    return fallback;
  } catch {}
  return body && !body.includes('<html') ? body : fallback;
}

function OcrLoadingState({title, detail, compact = false}: {title: string; detail: string; compact?: boolean}) {
  return <div className={`ocr-loading-state${compact ? ' is-compact' : ''}`} role="status" aria-live="polite">
    <div className="ocr-loading-page" aria-hidden="true"><span /></div>
    <strong>{title}</strong>
    <span>{detail}</span>
  </div>;
}

export default function OcrWorkspace({session, onUpdateSession, onDirtyChange, userId, apiKey}: Props) {
  const [data, setData] = useState<OcrSession | null>(null);
  const [drafts, setDrafts] = useState<Drafts>({});
  const draftsRef = useRef<Drafts>({});
  const [history, setHistory] = useState<Record<string, HistoryState>>({});
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [streamingActiveRegionIds, setStreamingActiveRegionIds] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [archivingDocumentId, setArchivingDocumentId] = useState<string | null>(null);
  const [loadingExample, setLoadingExample] = useState<string | null>(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState('');
  const [quotaRetry, setQuotaRetry] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'idle'>('idle');
  const [editorTab, setEditorTab] = useState<'region' | 'txt' | 'json'>('region');
  const [mobileView, setMobileView] = useState<'page' | 'text'>('page');
  const [mobilePeekRegionId, setMobilePeekRegionId] = useState<string | null>(null);
  const [provenanceOpen, setProvenanceOpen] = useState(false);
  const [regionLegendOpen, setRegionLegendOpen] = useState(false);
  const [tool, setTool] = useState<EditorTool>('select');
  const [gestureRegions, setGestureRegions] = useState<OcrRegion[] | null>(null);
  const [drawPreview, setDrawPreview] = useState<OcrRegion['bbox'] | null>(null);
  const [splitGuide, setSplitGuide] = useState<{axis: SplitAxis; position: number} | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [exampleViewportRef, exampleApi] = useEmblaCarousel({align: 'start', containScroll: 'trimSnaps', duration: 28, slidesToScroll: 1});
  const [canScrollExamplesBackward, setCanScrollExamplesBackward] = useState(false);
  const [canScrollExamplesForward, setCanScrollExamplesForward] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const textCursorRef = useRef(0);
  const processingRef = useRef(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);

  const updateExampleCarousel = useCallback(() => {
    if (!exampleApi) return;
    setCanScrollExamplesBackward(exampleApi.canScrollPrev());
    setCanScrollExamplesForward(exampleApi.canScrollNext());
  }, [exampleApi]);

  useEffect(() => {
    if (!exampleApi) return;
    updateExampleCarousel();
    exampleApi.on('reInit', updateExampleCarousel).on('select', updateExampleCarousel);
    return () => {
      exampleApi.off('reInit', updateExampleCarousel).off('select', updateExampleCarousel);
    };
  }, [exampleApi, updateExampleCarousel]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const showError = useCallback((message: string) => {
    setError(current => current.includes(message) ? current : [current, message].filter(Boolean).join('\n'));
  }, []);

  const setAllDrafts = useCallback((next: Drafts) => {
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const writeDocumentDraft = useCallback((documentId: string, pages: DraftPages) => {
    const next = {...draftsRef.current};
    if (Object.keys(pages).length) {
      next[documentId] = pages;
      try {
        saveOcrDraft(userId, session.id, documentId, pages);
      } catch {
        showError('Не вдалося зберегти локальну OCR-чернетку.');
      }
    } else {
      delete next[documentId];
      removeOcrDraft(userId, session.id, documentId);
    }
    setAllDrafts(next);
    setSaveState('idle');
  }, [session.id, setAllDrafts, showError, userId]);

  const restoreLocalDrafts = useCallback((next: OcrSession) => {
    const restored: Drafts = {};
    let discardedStale = false;
    next.documents.forEach(document => {
      const draft = loadOcrDraft(userId, session.id, document.id);
      if (!draft) return;
      const valid = Object.entries(draft.pages).every(([pageId, pageDraft]) => {
        const page = document.pages.find(candidate => candidate.id === pageId);
        return page && page.status === 'completed' && page.revision === pageDraft.baseRevision;
      });
      if (valid && Object.keys(draft.pages).length) restored[document.id] = draft.pages;
      else {
        removeOcrDraft(userId, session.id, document.id);
        discardedStale = true;
      }
    });
    setAllDrafts(restored);
    if (discardedStale) showError('Локальну чернетку відкинуто, бо документ уже оновлено на сервері.');
  }, [session.id, setAllDrafts, showError, userId]);

  const loadSession = useCallback(async () => {
    const response = await ocrFetch(`${CONFIG.ocrBackendUrl}/ocr/sessions/${session.id}?user_id=${encodeURIComponent(userId)}`);
    if (!response.ok) throw new Error(await responseError(response, 'Не вдалося завантажити OCR-сесію.'));
    const next: OcrSession = await response.json();
    setData(next);
    restoreLocalDrafts(next);
    const firstPage = next.documents[0]?.pages[0];
    if (firstPage) setSelectedPageId(current => current || firstPage.id);
    return next;
  }, [restoreLocalDrafts, session.id, userId]);

  useEffect(() => {
    setLoading(true);
    loadSession().catch(error => showError(error instanceof Error ? error.message : 'Не вдалося завантажити OCR-сесію.')).finally(() => setLoading(false));
  }, [loadSession, showError]);

  useEffect(() => {
    onDirtyChange?.(Object.values(drafts).some(pages => Object.keys(pages).length > 0));
  }, [drafts, onDirtyChange]);

  const processPages = useCallback(async (source?: OcrSession) => {
    if (processingRef.current) return;
    processingRef.current = true;
    setQuotaRetry(null);
    try {
      const snapshot = source || data || await loadSession();
      const queue = snapshot.documents.flatMap(document => document.pages)
        .filter(page => page.status === 'queued' || page.status === 'failed' || page.status === 'quota_paused')
        .map(page => page.id);
      let cursor = 0;
      let quotaStopped = false;
      const worker = async () => {
        while (!quotaStopped) {
          const pageId = queue[cursor++];
          if (!pageId) return;
          setData(current => {
            const page = current ? findPage(current, pageId) : undefined;
            return page ? replacePage(current, pageId, {...page, status: 'processing', error: undefined}) : current;
          });
          try {
            const headers: Record<string, string> = {'Content-Type': 'application/json'};
            if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
            const response = await ocrFetch(`${CONFIG.ocrBackendUrl}/ocr/pages/${pageId}/recognize/stream`, {
              method: 'POST', headers,
              body: JSON.stringify({user_id: userId, session_id: session.id}),
            });
            if (response.status === 409) continue;
            if (!response.ok) throw new Error(await response.text());
            const next = await readOcrStream(response, region => {
              setStreamingActiveRegionIds(current => ({...current, [pageId]: region.id}));
              setData(current => {
                const page = current ? findPage(current, pageId) : undefined;
                if (!page || page.regions.some(existing => existing.id === region.id)) return current;
                return replacePage(current, pageId, {...page, regions: [...page.regions, region]});
              });
            });
            const completed = findPage(next, pageId);
            if (completed) {
              setStreamingActiveRegionIds(current => {
                const nextActive = {...current};
                delete nextActive[pageId];
                return nextActive;
              });
              setData(current => replacePage(current, pageId, completed));
            }
          } catch (streamError) {
            const error = streamError as OcrStreamFailure;
            if (error.status === 429) {
              quotaStopped = true;
              setQuotaRetry(Number(error.retryAfter || 3600));
              return;
            }
            showError(error.status && error.message
              ? error.message
              : 'Не вдалося з’єднатися з OCR-сервісом. Незавершені сторінки можна запустити повторно.');
            setData(current => {
              const page = current ? findPage(current, pageId) : undefined;
              return page ? replacePage(current, pageId, {...page, status: 'failed', error: 'OCR-сервіс недоступний.'}) : current;
            });
            continue;
          }
        }
      };
      await Promise.all(Array.from({length: Math.min(PAGE_CONCURRENCY, queue.length)}, worker));
      await loadSession();
    } catch {
      showError('Обробку перервано. Незавершені сторінки можна запустити повторно.');
    } finally {
      processingRef.current = false;
    }
  }, [apiKey, data, loadSession, session.id, showError, userId]);

  const uploadFiles = async (files: File[]) => {
    const validationErrors = files.filter(file => file.size > MAX_FILE_BYTES)
      .map(file => `${file.name}: максимальний розмір — 50 MiB.`);
    const valid = files.filter(file => file.size <= MAX_FILE_BYTES);
    if (!valid.length) { showError(validationErrors.join('\n')); return; }
    setUploading(true);
    setError('');
    let latest: OcrSession | null = data;
    let uploadedAny = false;
    const uploadErrors = [...validationErrors];
    try {
      for (const file of valid) {
        try {
          const form = new FormData();
          form.append('user_id', userId);
          form.append('file', file);
          const response = await ocrFetch(`${CONFIG.ocrBackendUrl}/ocr/sessions/${session.id}/documents`, {method: 'POST', body: form});
          if (!response.ok) {
            uploadErrors.push(`${file.name}: ${await responseError(response, 'Не вдалося завантажити документ.')}`);
            continue;
          }
          const uploadedSession: OcrSession = await response.json();
          latest = uploadedSession;
          uploadedAny = true;
          setData(uploadedSession);
          const uploaded = uploadedSession.documents[uploadedSession.documents.length - 1];
          if (uploaded?.pages[0]) setSelectedPageId(uploaded.pages[0].id);
          if (session.label === 'Rukopys OCR' || session.label === 'Новий чат') {
            onUpdateSession({...session, label: `OCR: ${file.name}`.slice(0, 50)});
          }
        } catch {
          uploadErrors.push(`${file.name}: не вдалося з’єднатися із сервером.`);
        }
      }
      if (uploadedAny && latest) await processPages(latest);
      if (uploadErrors.length) showError(uploadErrors.join('\n'));
    } finally {
      setUploading(false);
    }
  };

  const loadExample = async (example: typeof OCR_EXAMPLES[number]) => {
    if (uploading || loadingExample) return;
    setLoadingExample(example.id);
    setError('');
    try {
      const response = await fetch(example.asset);
      if (!response.ok) throw new Error('Не вдалося завантажити приклад.');
      const blob = await response.blob();
      await uploadFiles([new File([blob], example.fileName, {type: example.mimeType})]);
    } catch (loadError) {
      showError(loadError instanceof Error ? loadError.message : 'Не вдалося завантажити приклад.');
    } finally {
      setLoadingExample(null);
    }
  };

  const archiveDocument = async (document: OcrDocument) => {
    if (archivingDocumentId) return;
    setArchivingDocumentId(document.id);
    try {
      const response = await ocrFetch(`${CONFIG.ocrBackendUrl}/ocr/documents/${document.id}/archive`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({user_id: userId, session_id: session.id}),
      });
      if (!response.ok) throw new Error(await responseError(response, 'Не вдалося прибрати документ з чату.'));
      const next: OcrSession = await response.json();
      removeOcrDraft(userId, session.id, document.id);
      const nextDrafts = {...draftsRef.current};
      delete nextDrafts[document.id];
      setAllDrafts(nextDrafts);
      setHistory(current => {
        const nextHistory = {...current};
        delete nextHistory[document.id];
        return nextHistory;
      });
      const nextPage = next.documents.flatMap(candidate => candidate.pages)[0];
      setSelectedPageId(nextPage?.id || null);
      setSelectedRegionId(null);
      setSelectedIds(new Set());
      setData(next);
    } catch (archiveError) {
      showError(archiveError instanceof Error ? archiveError.message : 'Не вдалося прибрати документ з чату.');
    } finally {
      setArchivingDocumentId(null);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  const activeDocument = useMemo(
    () => data?.documents.find(document => document.pages.some(page => page.id === selectedPageId)) || data?.documents[0],
    [data, selectedPageId],
  );
  const activePage = activeDocument?.pages.find(page => page.id === selectedPageId) || activeDocument?.pages[0];
  const activeDraftPages = activeDocument ? drafts[activeDocument.id] : undefined;
  const activeRegions = activePage ? effectiveRegions(activePage, activeDraftPages) : [];
  const visibleRegions = gestureRegions || activeRegions;
  const activeRegion = activeRegions.find(region => region.id === selectedRegionId) || activeRegions[0];
  const activeDocumentView = activeDocument ? effectiveDocument(activeDocument, activeDraftPages) : undefined;
  const hasActiveDraft = Boolean(activeDraftPages && Object.keys(activeDraftPages).length);
  const completedPdfPageCount = activeDocument?.pages.filter(page => page.status === 'completed').length || 0;
  const activeDocumentHasPdfPages = completedPdfPageCount > 0;
  const activeDocumentReadyForPdf = Boolean(activeDocumentHasPdfPages && activeDocument
    && completedPdfPageCount === activeDocument.pages.length);
  const activeHistory = activeDocument ? history[activeDocument.id] : undefined;

  useEffect(() => {
    setZoom(1);
    setTool('select');
    setSelectedIds(new Set());
    setMobilePeekRegionId(null);
  }, [activePage?.id]);

  useEffect(() => {
    if (!activeRegions.some(region => region.id === selectedRegionId)) {
      const first = activeRegions[0]?.id || null;
      if (selectedRegionId === first) return;
      setSelectedRegionId(first);
      setSelectedIds(first ? new Set([first]) : new Set());
    }
  }, [activeRegions, selectedRegionId]);

  const applyPageRegions = useCallback((page: OcrPage, regions: OcrRegion[], recordHistory = true) => {
    if (!activeDocument) return;
    const currentPages = draftsRef.current[activeDocument.id] || {};
    if (recordHistory) {
      const snapshot = cloneDraftPages(currentPages);
      setHistory(current => {
        const state = current[activeDocument.id] || {past: [], future: []};
        return {...current, [activeDocument.id]: {
          past: [...state.past, snapshot].slice(-HISTORY_LIMIT), future: [],
        }};
      });
    }
    const nextPages = {...currentPages};
    const ordered = sortRegions(regions);
    if (regionsEqual(ordered, page.regions)) delete nextPages[page.id];
    else nextPages[page.id] = {baseRevision: currentPages[page.id]?.baseRevision ?? page.revision, regions: cloneRegions(ordered)};
    writeDocumentDraft(activeDocument.id, nextPages);
  }, [activeDocument, writeDocumentDraft]);

  const undo = () => {
    if (!activeDocument || !activeHistory?.past.length) return;
    const previous = activeHistory.past[activeHistory.past.length - 1];
    const currentPages = cloneDraftPages(draftsRef.current[activeDocument.id] || {});
    setHistory(current => ({...current, [activeDocument.id]: {
      past: activeHistory.past.slice(0, -1), future: [currentPages, ...activeHistory.future].slice(0, HISTORY_LIMIT),
    }}));
    writeDocumentDraft(activeDocument.id, cloneDraftPages(previous));
  };

  const redo = () => {
    if (!activeDocument || !activeHistory?.future.length) return;
    const nextPages = activeHistory.future[0];
    const currentPages = cloneDraftPages(draftsRef.current[activeDocument.id] || {});
    setHistory(current => ({...current, [activeDocument.id]: {
      past: [...activeHistory.past, currentPages].slice(-HISTORY_LIMIT), future: activeHistory.future.slice(1),
    }}));
    writeDocumentDraft(activeDocument.id, cloneDraftPages(nextPages));
  };

  const discardDocument = () => {
    if (!activeDocument) return;
    writeDocumentDraft(activeDocument.id, {});
    setHistory(current => ({...current, [activeDocument.id]: {past: [], future: []}}));
    setSelectedRegionId(activePage?.regions[0]?.id || null);
    setSelectedIds(activePage?.regions[0]?.id ? new Set([activePage.regions[0].id]) : new Set());
  };

  const saveDocument = async () => {
    if (!activeDocument || !activeDraftPages || !Object.keys(activeDraftPages).length) return;
    setSaveState('saving');
    try {
      const response = await ocrFetch(`${CONFIG.ocrBackendUrl}/ocr/documents/${activeDocument.id}/corrections`, {
        method: 'PUT', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          user_id: userId, session_id: session.id,
          pages: Object.entries(activeDraftPages).map(([pageId, draft]) => ({
            page_id: pageId, expected_revision: draft.baseRevision, regions: draft.regions,
          })),
        }),
      });
      if (response.status === 409) {
        writeDocumentDraft(activeDocument.id, {});
        setHistory(current => ({...current, [activeDocument.id]: {past: [], future: []}}));
        await loadSession();
        showError('Локальну чернетку відкинуто, бо документ уже оновлено на сервері.');
        return;
      }
      if (!response.ok) throw new Error(await responseError(response, 'Не вдалося зберегти виправлення.'));
      const saved: OcrDocument = await response.json();
      setData(current => replaceDocument(current, saved));
      writeDocumentDraft(activeDocument.id, {});
      setHistory(current => ({...current, [activeDocument.id]: {past: [], future: []}}));
      setSaveState('saved');
    } catch (saveError) {
      setSaveState('idle');
      showError(saveError instanceof Error ? saveError.message : 'Не вдалося зберегти виправлення.');
    }
  };

  const downloadSearchablePdf = async () => {
    if (!activeDocument || pdfExporting) return;
    if (hasActiveDraft) {
      showError('Збережіть зміни документа перед завантаженням PDF.');
      return;
    }
    if (!activeDocumentHasPdfPages) {
      showError('PDF з текстовим шаром буде доступний після розпізнавання хоча б однієї сторінки.');
      return;
    }
    setPdfExporting(true);
    try {
      const response = await ocrFetch(
        `${CONFIG.ocrBackendUrl}/ocr/documents/${activeDocument.id}/searchable-pdf?user_id=${encodeURIComponent(userId)}&session_id=${encodeURIComponent(session.id)}`,
      );
      if (!response.ok) throw new Error(await responseError(response, 'Не вдалося сформувати PDF з текстовим шаром.'));
      downloadBlob(
        `${activeDocument.original_name.replace(/\.[^.]+$/, '') || 'document'}.pdf`,
        await response.blob(),
      );
    } catch (downloadError) {
      showError(downloadError instanceof Error ? downloadError.message : 'Не вдалося сформувати PDF з текстовим шаром.');
    } finally {
      setPdfExporting(false);
    }
  };

  const updateRegion = (patch: Partial<OcrRegion>) => {
    if (!activePage || !activeRegion || isMobile) return;
    applyPageRegions(activePage, activeRegions.map(region => region.id === activeRegion.id ? {...region, ...patch} : region));
  };

  const mergeSelected = () => {
    if (!activePage || selectedIds.size < 2 || isMobile) return;
    const selected = sortRegions(activeRegions.filter(region => selectedIds.has(region.id)));
    const merged = mergeRegions(activeRegions, selectedIds);
    applyPageRegions(activePage, merged);
    setSelectedRegionId(selected[0].id);
    setSelectedIds(new Set([selected[0].id]));
  };

  const deleteSelected = () => {
    if (!activePage || !selectedIds.size || isMobile) return;
    const remaining = activeRegions.filter(region => !selectedIds.has(region.id));
    applyPageRegions(activePage, remaining);
    const next = remaining[0]?.id || null;
    setSelectedRegionId(next);
    setSelectedIds(next ? new Set([next]) : new Set());
  };

  const restoreInitialPage = () => {
    if (!activePage || isMobile) return;
    applyPageRegions(activePage, cloneRegions(activePage.initial_regions || []));
    const first = activePage.initial_regions?.[0]?.id || null;
    setSelectedRegionId(first);
    setSelectedIds(first ? new Set([first]) : new Set());
  };

  const selectRegion = (regionId: string, additive = false) => {
    setSelectedRegionId(regionId);
    setSelectedIds(current => {
      if (!additive) return new Set([regionId]);
      const next = new Set(current);
      if (next.has(regionId)) next.delete(regionId); else next.add(regionId);
      return next;
    });
  };

  const pointerRegions = (gesture: Gesture, point: [number, number]): OcrRegion[] => {
    if (!gesture.region) return gesture.baseRegions;
    const deltaX = point[0] - gesture.start[0];
    const deltaY = point[1] - gesture.start[1];
    const replacement = gesture.mode === 'resize' && gesture.handle
      ? resizeRegion(gesture.region, gesture.handle, deltaX, deltaY)
      : moveRegion(gesture.region, deltaX, deltaY);
    return gesture.baseRegions.map(region => region.id === replacement.id ? replacement : region);
  };

  const beginRegionGesture = (event: ReactPointerEvent, region: OcrRegion, handle?: ResizeHandle) => {
    if (isMobile || tool !== 'select') return;
    event.preventDefault();
    event.stopPropagation();
    if (event.shiftKey) { selectRegion(region.id, true); return; }
    selectRegion(region.id);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const start = normalizedPoint(event.clientX, event.clientY, canvas.getBoundingClientRect());
    gestureRef.current = {mode: handle ? 'resize' : 'move', pointerId: event.pointerId, start, region, handle, baseRegions: cloneRegions(activeRegions)};
    canvas.setPointerCapture(event.pointerId);
  };

  const onCanvasPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isMobile || tool === 'select' || !canvasRef.current) return;
    const point = normalizedPoint(event.clientX, event.clientY, canvasRef.current.getBoundingClientRect());
    if (tool === 'split') {
      if (!activeRegion) return;
      const [x1, y1, x2, y2] = activeRegion.bbox;
      if (point[0] < x1 || point[0] > x2 || point[1] < y1 || point[1] > y2) return;
    }
    gestureRef.current = {mode: tool, pointerId: event.pointerId, start: point, baseRegions: cloneRegions(activeRegions)};
    canvasRef.current.setPointerCapture(event.pointerId);
  };

  const onCanvasPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || !canvasRef.current) return;
    const point = normalizedPoint(event.clientX, event.clientY, canvasRef.current.getBoundingClientRect());
    if (gesture.mode === 'move' || gesture.mode === 'resize') setGestureRegions(pointerRegions(gesture, point));
    if (gesture.mode === 'draw') setDrawPreview(drawBox(gesture.start, point));
    if (gesture.mode === 'split') {
      const axis: SplitAxis = Math.abs(point[0] - gesture.start[0]) >= Math.abs(point[1] - gesture.start[1]) ? 'horizontal' : 'vertical';
      setSplitGuide({axis, position: axis === 'horizontal' ? point[1] : point[0]});
    }
  };

  const finishPointerGesture = (event: ReactPointerEvent<HTMLDivElement>, cancelled = false) => {
    const gesture = gestureRef.current;
    if (!gesture || !canvasRef.current || !activePage) return;
    const point = normalizedPoint(event.clientX, event.clientY, canvasRef.current.getBoundingClientRect());
    if (!cancelled && (gesture.mode === 'move' || gesture.mode === 'resize')) {
      applyPageRegions(activePage, pointerRegions(gesture, point));
    }
    if (!cancelled && gesture.mode === 'draw') {
      const bbox = drawBox(gesture.start, point);
      if (bbox) {
        const created = createRegion(bbox, activeRegion?.type || 'handwritten');
        applyPageRegions(activePage, [...activeRegions, created]);
        setSelectedRegionId(created.id);
        setSelectedIds(new Set([created.id]));
      }
      setTool('select');
    }
    if (!cancelled && gesture.mode === 'split' && activeRegion) {
      const axis: SplitAxis = Math.abs(point[0] - gesture.start[0]) >= Math.abs(point[1] - gesture.start[1]) ? 'horizontal' : 'vertical';
      const position = axis === 'horizontal' ? point[1] : point[0];
      const result = splitRegion(activeRegions, activeRegion.id, axis, position, textCursorRef.current);
      if (result) {
        applyPageRegions(activePage, result);
        const splitIds = result.filter(region => region.id === activeRegion.id || !activeRegions.some(old => old.id === region.id)).map(region => region.id);
        setSelectedRegionId(splitIds[0] || null);
        setSelectedIds(new Set(splitIds));
      } else showError('Не вдалося розділити регіон у вибраному місці.');
      setTool('select');
    }
    if (canvasRef.current.hasPointerCapture(gesture.pointerId)) canvasRef.current.releasePointerCapture(gesture.pointerId);
    gestureRef.current = null;
    setGestureRegions(null);
    setDrawPreview(null);
    setSplitGuide(null);
  };

  useEffect(() => {
    const hasDetachedProcessing = Boolean(data?.documents.some(document =>
      document.pages.some(page => page.status === 'processing')));
    if (!hasDetachedProcessing || processingRef.current) return;

    let disposed = false;
    let polling = false;
    const pollInterruptedRecognition = async () => {
      if (disposed || polling || processingRef.current) return;
      polling = true;
      try {
        const snapshot = await loadSession();
        if (disposed) return;
        const pages = snapshot.documents.flatMap(document => document.pages);
        const hasProcessing = pages.some(page => page.status === 'processing');
        const hasRetryable = pages.some(page => page.status === 'queued' || page.status === 'failed');
        if (!hasProcessing || hasRetryable) setData(snapshot);
        if (hasRetryable) void processPages(snapshot);
      } catch {
        // Keep the current processing state and retry on the next polling tick.
      } finally {
        polling = false;
      }
    };

    void pollInterruptedRecognition();
    const intervalId = window.setInterval(() => void pollInterruptedRecognition(), 1000);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [data, loadSession, processPages]);

  const imageUrl = activePage
    ? `${CONFIG.ocrBackendUrl}/ocr/pages/${activePage.id}/image?user_id=${encodeURIComponent(userId)}&session_id=${encodeURIComponent(session.id)}` : '';
  const completedCount = data?.documents.flatMap(document => document.pages).filter(page => page.status === 'completed').length || 0;
  const totalCount = data?.documents.reduce((sum, document) => sum + document.page_count, 0) || 0;
  const pendingCount = data?.documents.flatMap(document => document.pages)
    .filter(page => page.status === 'queued' || page.status === 'failed' || page.status === 'quota_paused').length || 0;
  const failedCount = data?.documents.flatMap(document => document.pages).filter(page => page.status === 'failed').length || 0;
  const hasQuotaPaused = Boolean(quotaRetry) || Boolean(data?.documents.some(document => document.pages.some(page => page.status === 'quota_paused')));
  const usesSharedApiKey = !apiKey?.trim();
  const accountSignUpUrl = `https://accounts.lapathoniia.top/sign-up?redirect_url=${encodeURIComponent(window.location.href)}`;
  const mobilePeekRegion = activeRegions.find(region => region.id === mobilePeekRegionId);
  const mobilePeekRegionIndex = mobilePeekRegion ? activeRegions.findIndex(region => region.id === mobilePeekRegion.id) : -1;

  const showMobileText = () => {
    setEditorTab('txt');
    setMobileView('text');
  };

  return (
    <div className="ocr-workspace">
      <section className={`ocr-provenance${isMobile && !provenanceOpen ? ' is-collapsed' : ''}`} aria-label="Походження моделі Rukopys OCR">
        {isMobile && <button className="ocr-provenance-toggle" type="button" aria-expanded={provenanceOpen} onClick={() => setProvenanceOpen(value => !value)}>
          <span>Про модель Rukopys OCR</span><ChevronDown size={16} aria-hidden="true" />
        </button>}
        {(!isMobile || provenanceOpen) && <>
          <div className="ocr-provenance-art" aria-hidden="true">
            <img src="https://www.kaggle.com/competitions/130964/images/header" alt="" />
          </div>
          <div className="ocr-provenance-copy">
            <p>В основі Rukopys OCR — <a href="https://huggingface.co/ebinan92/Rukopys-OCR-4B" target="_blank" rel="noreferrer">Rukopys-OCR-4B <ExternalLink size={12} aria-hidden="true" /></a>, створена в межах <a href="https://www.kaggle.com/competitions/handwritten-to-data" target="_blank" rel="noreferrer">Handwritten to Data <ExternalLink size={12} aria-hidden="true" /></a>.</p>
            <p>Автор: <a href="https://huggingface.co/ebinan92" target="_blank" rel="noreferrer">Тецуя Фудзімура (ebinan92)</a> · дані: <a href="https://huggingface.co/datasets/UkrainianCatholicUniversity/rukopys" target="_blank" rel="noreferrer">Rukopys <ExternalLink size={12} aria-hidden="true" /></a></p>
          </div>
        </>}
      </section>
      <header className="ocr-toolbar">
        <div className="ocr-title">
          <strong>Rukopys OCR</strong>
          <span className="ocr-lock" title="Щоб змінити модель, створіть новий чат">🔒 OCR</span>
          {totalCount > 0 && <span className="ocr-progress-count">{completedCount}/{totalCount}</span>}
          {hasActiveDraft && <span className="ocr-unsaved-state">Незбережені зміни</span>}
        </div>
        <div className="ocr-toolbar-actions">
          {!isMobile && activeDocument && (
            <>
              <button className="ocr-icon-button" title="Скасувати дію" aria-label="Скасувати дію" disabled={!activeHistory?.past.length} onClick={undo}><Undo2 size={16} /></button>
              <button className="ocr-icon-button" title="Повторити дію" aria-label="Повторити дію" disabled={!activeHistory?.future.length} onClick={redo}><Redo2 size={16} /></button>
              <button className="ocr-icon-button" title="Відкинути зміни документа" aria-label="Відкинути зміни документа" disabled={!hasActiveDraft || saveState === 'saving'} onClick={discardDocument}><RotateCcw size={16} /></button>
              <button className="ocr-save-button" disabled={!hasActiveDraft || saveState === 'saving'} onClick={() => void saveDocument()}>
                <Save size={15} /> {saveState === 'saving' ? 'Збереження…' : 'Зберегти документ'}
              </button>
            </>
          )}
          {pendingCount > 0 && !uploading && !isMobile && <button className="ocr-secondary-button" onClick={() => void processPages()} title="Продовжити обробку"><RefreshCw size={14} /> Продовжити ({pendingCount})</button>}
          <button className="ocr-secondary-button" onClick={() => void downloadSearchablePdf()} disabled={!activeDocument || pdfExporting} aria-busy={pdfExporting}
            title={hasActiveDraft ? 'Спочатку збережіть документ' : !activeDocumentHasPdfPages ? 'PDF буде доступний після розпізнавання хоча б однієї сторінки' : !activeDocumentReadyForPdf ? `PDF міститиме текстовий шар для ${completedPdfPageCount} з ${activeDocument?.pages.length} готових сторінок` : 'Завантажити PDF з текстовим шаром'}>
            {pdfExporting ? <LoaderCircle className="ocr-button-spinner" size={15} /> : <Download size={15} />} {pdfExporting ? 'Формування PDF…' : 'PDF'}
          </button>
          <button className="ocr-upload-button" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Upload size={15} /> {uploading ? 'Завантаження…' : 'Документ'}
          </button>
        </div>
      </header>

      {error && <div className="msg-bubble ocr-notice is-error" role="alert">{error}<button onClick={() => setError('')} aria-label="Закрити">×</button></div>}
      {hasQuotaPaused && (
        <div className="msg-bubble ocr-notice is-warning" role="status">
          <span>Квоту зображень вичерпано. Готові сторінки збережені; решту можна продовжити після оновлення квоти.</span>
          {usesSharedApiKey && (
            <a className="ocr-notice-cta" href={accountSignUpUrl} target="_blank" rel="noreferrer">
              Створіть власний акаунт для використання індивідуальних лімітів
            </a>
          )}
        </div>
      )}
      {failedCount > 0 && <div className="msg-bubble ocr-notice is-error" role="status">Не вдалося розпізнати сторінок: {failedCount}. Їх можна запустити повторно кнопкою «Продовжити».</div>}

      {loading ? <div className="ocr-empty"><OcrLoadingState title="Відкриваємо OCR-сесію" detail="Завантажуємо збережені документи" /></div> : !data?.documents.length ? (
        <div className="ocr-empty">
          {uploading ? <OcrLoadingState title="Завантажуємо документ" detail="Готуємо сторінки до розпізнавання" /> : <>
            <div className={`ocr-dropzone${dragging ? ' is-dragging' : ''}`}
              onDragOver={event => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={onDrop} onClick={() => inputRef.current?.click()}>
              <Upload className="ocr-drop-icon" size={32} />
              <strong>Завантажте рукопис або скан</strong>
              <span>PNG, JPEG, WebP, PDF або TIFF · до 50 MiB · до 256 сторінок</span>
            </div>
            <p className="ocr-storage-note">Документи та результати зберігаються на сервері після видалення чату.</p>
            <section className="ocr-example-library" aria-labelledby="ocr-examples-title">
              <div className="ocr-example-heading" id="ocr-examples-title">
                <span><BookOpen size={16} aria-hidden="true" /> Спробуйте на прикладі</span>
                <small>Кожна сторінка = окремий OCR-запит</small>
              </div>
              <div className="ocr-example-carousel">
                <button className="ocr-example-arrow" type="button" title="Попередні приклади" aria-label="Попередні приклади"
                  disabled={!canScrollExamplesBackward} onClick={() => exampleApi?.scrollPrev()}><ChevronLeft size={18} /></button>
                <div className="ocr-example-viewport" ref={exampleViewportRef}>
                  <div className="ocr-example-track">
                    {OCR_EXAMPLES.map(example => (
                      <article className="ocr-example-slide" key={example.id}>
                        <button className="ocr-example-card" type="button" onClick={() => void loadExample(example)} disabled={Boolean(uploading || loadingExample)}>
                          <img src={example.preview} alt={`Фрагмент рукопису: ${example.author}, ${example.title}`} />
                          <strong>{example.title}, {example.author}</strong>
                        </button>
                        <a className="ocr-example-source" href={example.sourceUrl} target="_blank" rel="noreferrer">Джерело <ExternalLink size={12} aria-hidden="true" /></a>
                      </article>
                    ))}
                  </div>
                </div>
                <button className="ocr-example-arrow" type="button" title="Наступні приклади" aria-label="Наступні приклади"
                  disabled={!canScrollExamplesForward} onClick={() => exampleApi?.scrollNext()}><ChevronRight size={18} /></button>
              </div>
            </section>
          </>}
        </div>
      ) : (
        <div className="ocr-main">
          <aside className="ocr-page-rail">
            {data.documents.map(document => (
              <section key={document.id} className="ocr-document-group">
                <div className="ocr-document-heading">
                  <span className="ocr-document-name" title={document.original_name}>
                    {document.original_name}{drafts[document.id] && <span className="ocr-dirty-marker" title="Незбережені зміни">•</span>}
                  </span>
                  <button className="ocr-archive-button" type="button" disabled={archivingDocumentId !== null}
                    aria-label={`Прибрати ${document.original_name} з чату`} title="Прибрати з чату"
                    onClick={() => void archiveDocument(document)}>
                    {archivingDocumentId === document.id ? '…' : '×'}
                  </button>
                </div>
                <div className="ocr-page-list">
                  {document.pages.map(page => (
                    <button key={page.id} className={`ocr-page-item${activePage?.id === page.id ? ' is-active' : ''}`}
                      onClick={() => setSelectedPageId(page.id)}
                      aria-label={`${document.original_name}, сторінка ${page.page_index + 1}: ${PAGE_STATUS_LABELS[page.status]}`}>
                      <span>Сторінка {page.page_index + 1}{drafts[document.id]?.[page.id] ? ' •' : ''}</span>
                      <span className={`ocr-status is-${page.status}`} title={PAGE_STATUS_LABELS[page.status]} aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </aside>

          {isMobile && <div className="ocr-mobile-view-tabs" role="tablist" aria-label="Перегляд результату OCR">
            <button type="button" role="tab" aria-selected={mobileView === 'page'} className={mobileView === 'page' ? 'is-active' : ''}
              onClick={() => setMobileView('page')}>Сторінка</button>
            <button type="button" role="tab" aria-selected={mobileView === 'text'} className={mobileView === 'text' ? 'is-active' : ''}
              disabled={activePage?.status !== 'completed'} onClick={showMobileText}>Текст</button>
          </div>}

          {isMobile && mobileView === 'page' && activePage?.status === 'completed' && (
            <div className="ocr-mobile-status" role="status">Сторінку розпізнано. Перегляньте текст.</div>
          )}

          <main className={`ocr-preview-panel${isMobile && mobileView !== 'page' ? ' is-mobile-hidden' : ''}`}>
            {activePage && <>
              <div className="ocr-preview-toolbar">
                <span>Сторінка {activePage.page_index + 1}</span>
                <div className="ocr-preview-tools">
                  {!isMobile && activePage.status === 'completed' && <>
                    <button className={tool === 'select' ? 'is-active' : ''} title="Вибір і переміщення" aria-label="Вибір і переміщення" onClick={() => setTool('select')}><MousePointer2 size={15} /></button>
                    <button className={tool === 'draw' ? 'is-active' : ''} title="Новий регіон" aria-label="Новий регіон" onClick={() => setTool('draw')}><SquarePlus size={15} /></button>
                    <button title="Об’єднати вибрані регіони" aria-label="Об’єднати вибрані регіони" disabled={selectedIds.size < 2} onClick={mergeSelected}><Combine size={15} /></button>
                    <button className={tool === 'split' ? 'is-active' : ''} title="Розділити вибраний регіон" aria-label="Розділити вибраний регіон" disabled={!activeRegion} onClick={() => setTool('split')}><Split size={15} /></button>
                    <button title="Видалити вибрані регіони" aria-label="Видалити вибрані регіони" disabled={!selectedIds.size} onClick={deleteSelected}><Trash2 size={15} /></button>
                  </>}
                  <button title="Показати або сховати регіони" aria-label="Показати або сховати регіони" onClick={() => setOverlayVisible(value => !value)}>{overlayVisible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button title="Зменшити" aria-label="Зменшити" onClick={() => setZoom(value => Math.max(0.7, value - 0.2))}><ZoomOut size={15} /></button>
                  <span>{Math.round(zoom * 100)}%</span>
                  <button title="Збільшити" aria-label="Збільшити" onClick={() => setZoom(value => Math.min(2, value + 0.2))}><ZoomIn size={15} /></button>
                </div>
              </div>
              <div className="ocr-preview-scroll">
                <div ref={canvasRef} className={`ocr-page-canvas is-tool-${tool}`} style={{width: `${zoom * 100}%`}}
                  onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove}
                  onPointerUp={event => finishPointerGesture(event)} onPointerCancel={event => finishPointerGesture(event, true)}>
                  <img src={imageUrl} crossOrigin="use-credentials" alt={`Сторінка ${activePage.page_index + 1}`} draggable={false} />
                  {overlayVisible && (activePage.status === 'completed' || activePage.status === 'processing') && visibleRegions.map(region => (
                    <div key={region.id} className={`ocr-bbox${selectedIds.has(region.id) || (activePage.status === 'processing' && streamingActiveRegionIds[activePage.id] === region.id) ? ' is-selected' : ''}${activePage.status === 'processing' ? ' is-streaming' : ''}`}
                      style={{left: `${region.bbox[0] / 10}%`, top: `${region.bbox[1] / 10}%`, width: `${(region.bbox[2] - region.bbox[0]) / 10}%`, height: `${(region.bbox[3] - region.bbox[1]) / 10}%`, borderColor: REGION_COLORS[region.type]}}
                      title={`${REGION_LABELS[region.type]}${region.text ? `: ${region.text}` : ''}`}
                      role={activePage.status === 'completed' ? 'button' : undefined} tabIndex={activePage.status === 'completed' ? 0 : -1} aria-label={`${REGION_LABELS[region.type]}: ${region.text || 'без тексту'}`}
                      onKeyDown={activePage.status === 'completed' ? event => { if (event.key === 'Enter' || event.key === ' ') selectRegion(region.id); } : undefined}
                      onPointerDown={activePage.status === 'completed' ? event => beginRegionGesture(event, region) : undefined} onClick={activePage.status === 'completed' ? () => {
                        selectRegion(region.id);
                        if (isMobile) setMobilePeekRegionId(region.id);
                      } : undefined}>
                      {!isMobile && tool === 'select' && selectedRegionId === region.id && RESIZE_HANDLES.map(handle => (
                        <span key={handle} className={`ocr-resize-handle is-${handle}`} onPointerDown={event => beginRegionGesture(event, region, handle)} />
                      ))}
                    </div>
                  ))}
                  {drawPreview && <div className="ocr-draw-preview" style={{left: `${drawPreview[0] / 10}%`, top: `${drawPreview[1] / 10}%`, width: `${(drawPreview[2] - drawPreview[0]) / 10}%`, height: `${(drawPreview[3] - drawPreview[1]) / 10}%`}} />}
                  {splitGuide && activeRegion && <div className={`ocr-split-guide is-${splitGuide.axis}`} style={splitGuide.axis === 'horizontal'
                    ? {left: `${activeRegion.bbox[0] / 10}%`, top: `${splitGuide.position / 10}%`, width: `${(activeRegion.bbox[2] - activeRegion.bbox[0]) / 10}%`}
                    : {left: `${splitGuide.position / 10}%`, top: `${activeRegion.bbox[1] / 10}%`, height: `${(activeRegion.bbox[3] - activeRegion.bbox[1]) / 10}%`}} />}
                  {activePage.status === 'queued' && <div className="ocr-page-state is-loading"><OcrLoadingState compact title="Готуємо сторінку" detail="Додаємо її до черги OCR" /></div>}
                  {activePage.status === 'processing' && <div className="ocr-page-state is-loading"><OcrLoadingState compact title="Розпізнаємо сторінку" detail="Текст і регіони з’являться тут" /></div>}
                  {activePage.status === 'quota_paused' && <div className="ocr-page-state is-warning">Призупинено через квоту</div>}
                  {activePage.status === 'failed' && <div className="ocr-page-state is-error">Помилка OCR</div>}
                </div>
              </div>
            </>}
          </main>

          {isMobile && mobileView === 'page' && activePage?.status === 'completed' && mobilePeekRegion && (
            <aside className="ocr-mobile-region-peek" aria-live="polite">
              <strong>Регіон {mobilePeekRegionIndex + 1} з {activeRegions.length}</strong>
              <p>{mobilePeekRegion.text || 'У цьому регіоні немає тексту.'}</p>
              <div>
                <button type="button" onClick={() => navigator.clipboard.writeText(mobilePeekRegion.text)}><Copy size={14} /> Копіювати</button>
                <button type="button" onClick={showMobileText}>Показати весь текст</button>
              </div>
            </aside>
          )}

          <aside className={`ocr-editor${isMobile && mobileView !== 'text' ? ' is-mobile-hidden' : ''}`}>
            {activeDocumentView && activePage ? <>
              <div className="ocr-editor-tabs" role="tablist" aria-label="Результат OCR">
                <button role="tab" aria-selected={editorTab === 'region'} className={editorTab === 'region' ? 'is-active' : ''} onClick={() => setEditorTab('region')}>Регіон</button>
                <button role="tab" aria-selected={editorTab === 'txt'} className={editorTab === 'txt' ? 'is-active' : ''} onClick={() => setEditorTab('txt')}>TXT</button>
                <button role="tab" aria-selected={editorTab === 'json'} className={editorTab === 'json' ? 'is-active' : ''} onClick={() => setEditorTab('json')}>JSON</button>
              </div>

              {editorTab === 'region' && activePage.status === 'completed' && activeRegion && (
                <div role="tabpanel" className="ocr-editor-pane">
                  <div className="ocr-editor-heading">
                    <strong>Регіон {activeRegions.findIndex(region => region.id === activeRegion.id) + 1} з {activeRegions.length}</strong>
                    <span>{saveState === 'saving' ? 'Збереження…' : saveState === 'saved' ? 'Збережено' : hasActiveDraft ? 'Чернетка' : activePage.edited ? 'Збережено' : ''}</span>
                  </div>
                  <div className="ocr-type-field">
                    <div className="ocr-field-heading">
                      <label htmlFor={`ocr-region-type-${activePage.id}`}>Тип</label>
                      <button type="button" className="ocr-info-button" aria-label="Пояснення кольорів регіонів"
                        aria-expanded={regionLegendOpen} title="Пояснення кольорів" onClick={() => setRegionLegendOpen(value => !value)}>i</button>
                    </div>
                    <select id={`ocr-region-type-${activePage.id}`} value={activeRegion.type} disabled={isMobile}
                      onChange={event => updateRegion({type: event.target.value as OcrRegion['type']})}>
                      {REGION_TYPES.map(type => <option key={type} value={type}>{REGION_LABELS[type]}</option>)}
                    </select>
                    {regionLegendOpen && <div className="ocr-region-legend" role="note" aria-label="Кольори типів регіонів">
                      {REGION_TYPES.map(type => <div key={type}><span style={{background: REGION_COLORS[type]}} aria-hidden="true" /><strong>{REGION_LABELS[type]}</strong><code>{type}</code></div>)}
                    </div>}
                  </div>
                  <label>Текст
                    <textarea ref={textRef} value={activeRegion.text} readOnly={isMobile} onChange={event => {
                      textCursorRef.current = event.target.selectionStart;
                      updateRegion({text: event.target.value});
                    }} onSelect={event => { textCursorRef.current = event.currentTarget.selectionStart; }} rows={8} />
                  </label>
                  <div className="ocr-editor-actions">
                    <button title="Копіювати текст" onClick={() => navigator.clipboard.writeText(activeRegion.text)}><Copy size={14} /> Копіювати</button>
                    {!isMobile && <button title="Відновити початковий OCR сторінки" onClick={restoreInitialPage}><RotateCcw size={14} /> Початковий OCR</button>}
                  </div>
                  <div className="ocr-region-list">
                    {activeRegions.map((region, index) => <div key={region.id} className={`ocr-region-row${selectedIds.has(region.id) ? ' is-active' : ''}`}>
                      {!isMobile && <input type="checkbox" checked={selectedIds.has(region.id)} aria-label={`Вибрати регіон ${index + 1}`}
                        onChange={() => selectRegion(region.id, true)} />}
                      <button className="ocr-region-select" onClick={() => selectRegion(region.id)}>
                        <span style={{background: REGION_COLORS[region.type]}} title={`${REGION_LABELS[region.type]} (${region.type})`} />
                        <strong>{index + 1}</strong><em className="ocr-region-text">{region.text || region.type}</em>
                      </button>
                    </div>)}
                  </div>
                </div>
              )}
              {editorTab === 'region' && activePage.status !== 'completed' && <div role="tabpanel" className="ocr-editor-empty">Результат сторінки з’явиться після розпізнавання.</div>}
              {editorTab === 'region' && activePage.status === 'completed' && !activeRegion && <div role="tabpanel" className="ocr-editor-empty">На сторінці немає регіонів. Додайте регіон інструментом на зображенні.</div>}
              {editorTab === 'txt' && <div role="tabpanel" className="ocr-export-pane">
                <div className="ocr-export-heading"><strong>Повний текст</strong>
                  <button disabled={hasActiveDraft} title={hasActiveDraft ? 'Спочатку збережіть документ' : 'Завантажити TXT'}
                    onClick={() => download(`${activeDocumentView.original_name}.txt`, documentText(activeDocumentView), 'text/plain')}><Download size={14} /> TXT</button>
                </div>
                <pre>{documentText(activeDocumentView) || 'Розпізнаний текст ще відсутній.'}</pre>
              </div>}
              {editorTab === 'json' && <div role="tabpanel" className="ocr-export-pane">
                <div className="ocr-export-heading"><strong>JSON</strong>
                  <button disabled={hasActiveDraft} title={hasActiveDraft ? 'Спочатку збережіть документ' : 'Завантажити JSON'}
                    onClick={() => download(`${activeDocumentView.original_name}.json`, documentJson(activeDocumentView), 'application/json')}><Download size={14} /> JSON</button>
                </div>
                <pre>{documentJson(activeDocumentView)}</pre>
              </div>}
            </> : <div className="ocr-editor-empty">Результат сторінки з’явиться після розпізнавання.</div>}
          </aside>

          {isMobile && <div className="ocr-mobile-actionbar">
            {activePage?.status === 'processing' ? (
              <button type="button" disabled><LoaderCircle className="ocr-button-spinner" size={16} /> Розпізнаємо {completedCount} із {totalCount}</button>
            ) : pendingCount > 0 && !uploading ? (
              <button type="button" onClick={() => void processPages()}><RefreshCw size={15} /> Продовжити ({pendingCount})</button>
            ) : mobileView === 'page' && activePage?.status === 'completed' ? (
              <button type="button" onClick={showMobileText}>Переглянути текст</button>
            ) : mobileView === 'text' ? (
              <button type="button" onClick={() => setMobileView('page')}>Назад до сторінки</button>
            ) : null}
          </div>}
        </div>
      )}

      <input ref={inputRef} type="file" accept={ACCEPTED_FILES} multiple hidden
        onChange={event => { void uploadFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
    </div>
  );
}
